<div class="dashboard">

  <!-- HEADER -->
  <header class="dashboard-header">
    <div>
      <h1 class="dashboard-header__title">Dashboard</h1>
      <p class="dashboard-header__sub">Panoramica conversazioni · Settimana corrente</p>
    </div>
  </header>

  <!-- ERROR BANNER -->
  @if (error) {
    <div class="error-banner">
      <i class="pi pi-exclamation-triangle"></i>
      {{ error }}
    </div>
  }

  <!-- FILTER BAR -->
  <div class="filter-bar">
    <div class="week-nav">
      <button class="week-nav__arrow" (click)="goToPrevWeek()" [disabled]="isOldestWeek || isLoading">
        <i class="pi pi-chevron-left"></i>
      </button>
      <div class="week-nav__center">
        <span class="week-nav__label">{{ currentWeekLabel }}</span>
        <span class="week-nav__range">{{ currentWeekRange }}</span>
      </div>
      <button class="week-nav__arrow" (click)="goToNextWeek()" [disabled]="isNewestWeek || isLoading">
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>

    <div class="filter-sep"></div>

    <div class="op-chips">
      @for (op of allOperators; track op.id) {
        <button
          type="button"
          class="op-chip"
          [class.op-chip--active]="isOperatorSelected(op.id)"
          [disabled]="isLoading"
          (click)="toggleOperator(op)"
        >
          <span class="op-chip__initials">{{ op.initials }}</span>
          <span class="op-chip__name">{{ op.firstName }} {{ op.lastName[0] }}.</span>
        </button>
      }
    </div>

    @if (hasFilters) {
      <button type="button" class="clear-btn" (click)="clearFilters()" [disabled]="isLoading">
        <i class="pi pi-times"></i>
        Azzera
      </button>
    }

    @if (isLoading) {
      <span class="loading-indicator"><i class="pi pi-spinner pi-spin"></i></span>
    }
  </div>

  <!-- KPI GRID -->
  <section class="kpi-grid">
    @for (kpi of kpis; track kpi.label) {
      <div class="kpi-card">
        <div class="kpi-card__top">
          <span class="kpi-card__label">{{ kpi.label }}</span>
          <div class="kpi-card__icon-wrap">
            <i [class]="kpi.icon"></i>
          </div>
        </div>
        <div class="kpi-card__value">{{ kpi.value }}</div>
        <div class="kpi-card__footer">
          <span class="kpi-card__sub">{{ kpi.sub }}</span>
          @if (kpi.trendGood !== null) {
            <span class="kpi-trend" [class.kpi-trend--good]="kpi.trendGood" [class.kpi-trend--bad]="!kpi.trendGood">
              <i [class]="kpi.trendGood ? 'pi pi-arrow-up-right' : 'pi pi-arrow-down-right'"></i>
              {{ kpi.trendText }}
            </span>
          }
        </div>
      </div>
    }
  </section>

  <!-- ROW 1: trend line + doughnut -->
  <div class="charts-row charts-row--main">
    <p-card styleClass="chart-card">
      <ng-template pTemplate="header">
        <div class="chart-header">
          <div>
            <h3 class="chart-header__title">Andamento settimanale</h3>
            <p class="chart-header__sub">{{ trendSubtitle }}</p>
          </div>
        </div>
      </ng-template>
      <p-chart type="line" [data]="trendData" [options]="lineChartOptions" height="280px" />
    </p-card>

    <p-card styleClass="chart-card">
      <ng-template pTemplate="header">
        <div class="chart-header">
          <div>
            <h3 class="chart-header__title">Distribuzione per stato</h3>
            <p class="chart-header__sub">Tutte le conversazioni del team</p>
          </div>
        </div>
      </ng-template>
      <p-chart type="doughnut" [data]="statusDoughnutData" [options]="doughnutChartOptions" height="280px" />
    </p-card>
  </div>

  <!-- ROW 2: operator bar charts -->
  <div class="charts-row charts-row--equal">
    <p-card styleClass="chart-card">
      <ng-template pTemplate="header">
        <div class="chart-header">
          <div>
            <h3 class="chart-header__title">Conversazioni per operatore</h3>
            <p class="chart-header__sub">
              @if (selectedOperatorIds.length > 0) { {{ selectedOperatorIds.length }} operatori selezionati }
              @else { Volume di lavoro corrente }
            </p>
          </div>
        </div>
      </ng-template>
      <p-chart type="bar" [data]="operatorBarData" [options]="barChartOptions" height="240px" />
    </p-card>

    <p-card styleClass="chart-card">
      <ng-template pTemplate="header">
        <div class="chart-header">
          <div>
            <h3 class="chart-header__title">Tempo medio risposta</h3>
            <p class="chart-header__sub">
              @if (selectedOperatorIds.length > 0) { Confronto operatori selezionati }
              @else { In minuti per operatore }
            </p>
          </div>
        </div>
      </ng-template>
      <p-chart type="bar" [data]="responseTimeData" [options]="barChartOptions" height="240px" />
    </p-card>
  </div>

  <!-- ROW 3: table + operators panel -->
  <div class="bottom-row">
    <p-card styleClass="chart-card">
      <ng-template pTemplate="header">
        <div class="chart-header">
          <div>
            <h3 class="chart-header__title">Ultime conversazioni</h3>
            <p class="chart-header__sub">
              @if (selectedOperatorIds.length > 0) {
                Filtrate per {{ selectedOperatorIds.length }} operatore/i
              } @else {
                Attività recente
              }
            </p>
          </div>
          @if (selectedOperatorIds.length > 0) {
            <p-tag [value]="recentConversations.length + ' risultati'" severity="info" />
          }
        </div>
      </ng-template>
      <p-table [value]="recentConversations" responsiveLayout="scroll" [rowHover]="true" styleClass="dash-table">
        <ng-template pTemplate="header">
          <tr>
            <th>Nome</th>
            <th>Contatto</th>
            <th>Operatore</th>
            <th>Stato</th>
            <th>Priorità</th>
            <th>Attività</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td><span class="table-name">{{ row.contactName }}</span></td>
            <td><span class="table-phone">{{ row.phoneNumber }}</span></td>
            <td>{{ row.operatorName || '—' }}</td>
            <td>
              <span class="status-cell" [ngClass]="'status-cell--' + getStatusKey(row.status)">
                <i [class]="getStatusIcon(row.status)"></i>
                {{ getStatusLabel(row.status) }}
              </span>
            </td>
            <td>
              <i [class]="getPriorityIcon(row.priority)" [ngClass]="'priority--' + getPriorityKey(row.priority)" pTooltip="{{ getPriorityLabel(row.priority) }}"></i>
            </td>
            <td><span class="table-time">{{ row.lastActivityAt | date:'dd/MM, HH:mm' }}</span></td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6" class="empty-msg">Nessuna conversazione per gli operatori selezionati.</td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>

    <p-card styleClass="chart-card">
      <ng-template pTemplate="header">
        <div class="chart-header">
          <div>
            <h3 class="chart-header__title">Team operatori</h3>
            <p class="chart-header__sub">{{ onlineCount }} disponibili · {{ offlineCount }} offline</p>
          </div>
          <p-tag [value]="onlineCount + ' online'" severity="success" />
        </div>
      </ng-template>
      <div class="operators-list">
        @for (op of displayOperators; track op.id) {
          <div class="operator-item" [class.operator-item--selected]="isOperatorSelected(op.id)">
            <div class="operator-avatar">{{ op.initials }}</div>
            <div class="operator-details">
              <span class="operator-name">{{ op.firstName }} {{ op.lastName }}</span>
              <span class="operator-meta">
                @if (op.status === 'offline') { Non disponibile }
                @else { {{ op.conversationsCount }} conv · {{ op.avgResponseTimeMin }} min }
              </span>
            </div>
            <span class="status-dot status-dot--{{ op.status }}"></span>
          </div>
        }
      </div>
    </p-card>
  </div>

</div>
